import { useEffect, useRef } from 'react';

/**
 * @function useWebChatFocusOrder
 * Ensures the 3rd-party web chat launcher (e.g. Genesys) receives keyboard focus
 * @param {Object}  options
 * @param {string}  [options.chatSelector='#custom-launcher']  CSS selector for chat button
 * @param {string}  [options.launcherId='launcher']            ID of the chat launcher container
 * @param {string}  [options.faqId='faq-section']              ID of the FAQ section
 * @param {string}  [options.footerId='footer']                ID of the footer
 */
export default function useWebChatFocusOrder({
  chatSelector = '#custom-launcher',
  launcherId = 'launcher',
  faqId = 'faq-section',
  footerId = 'footer',
} = {}) {
  const cleanupRef = useRef(null);

  useEffect(() => {
    function getAnchor() {
      const faq = document.getElementById(faqId);
      if (faq) return faq;
      return document.getElementById(footerId) || null;
    }

    function getClosestBgColor(element) {
      let current = element?.parentElement;
      while (current && current !== document.documentElement) {
        const bg = window.getComputedStyle(current).backgroundColor;
        if (bg && bg !== 'transparent' && !bg.includes('rgba(0, 0, 0, 0)')) {
          return bg;
        }
        current = current.parentElement;
      }
      return '#000000';
    }

    // function toHex(rgb) {
    //   if (rgb.startsWith('#')) return rgb;
    //   const parts = rgb.match(/\d+/g);
    //   if (!parts) return '#000000';
    //   return `#${parts
    //     .slice(0, 3)
    //     .map(n => parseInt(n, 10).toString(16).padStart(2, '0'))
    //     .join('')}`;
    // }

    // function isLight(hex) {
    //   const cleanHex = hex.replace('#', '');
    //   const r = parseInt(cleanHex.substring(0, 2), 16);
    //   const g = parseInt(cleanHex.substring(2, 4), 16);
    //   const b = parseInt(cleanHex.substring(4, 6), 16);
    //   return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
    // }

    function setup(chatButton) {
      const anchor = getAnchor();
      if (!anchor) return;

      const launcher =
        document.getElementById(launcherId) ||
        chatButton.closest(`#${launcherId}`) ||
        chatButton.parentElement;

      const originalParent = launcher.parentNode;
      const originalNext = launcher.nextSibling;

      anchor.parentNode.insertBefore(launcher, anchor);

      // function onFocus() {
      //   requestAnimationFrame(() => {
      //     // Only show indicator for keyboard focus
      //     try {
      //       if (!chatButton.matches(':focus-visible')) return;
      //     } catch (_) {
      //       // :focus-visible not supported — always show
      //     }

      //     const bg = getClosestBgColor(chatButton);
      //     const hex = toHex(bg);
      //     const color = isLight(hex) ? '#dd1d78' : '#F5EBDA';

      //     chatButton.style.setProperty('outline', `3px solid ${color}`, 'important');
      //     chatButton.style.setProperty('outline-offset', '3px', 'important');
      //   });
      // }

      // function onBlur() {
      //   chatButton.style.removeProperty('outline');
      //   chatButton.style.removeProperty('outline-offset');
      // }
      function onFocus() {
        requestAnimationFrame(() => {
          // Only show indicator for keyboard focus
          try {
            if (!chatButton.matches(':focus-visible')) return;
          } catch (_) {
            // :focus-visible not supported — always show
          }

          const bg = getClosestBgColor(chatButton) || 'rgb(255, 255, 255)';

          // ✅ Fix for inline elements like <a> wrapping an image
          if (window.getComputedStyle(chatButton).display === 'inline') {
            chatButton.style.setProperty('display', 'inline-block', 'important');
          }

          chatButton.style.setProperty('outline', 'none', 'important');
          chatButton.style.setProperty(
            'box-shadow',
            `0 0 0 3px ${bg}, 0 0 0 5px #FFFFFF, 0 0 0 7px #493553`,
            'important'
          );
          chatButton.style.setProperty('border-radius', '50%', 'important');
        });
      }

      function onBlur() {
        chatButton.style.removeProperty('outline');
        chatButton.style.removeProperty('outline-offset');
        chatButton.style.removeProperty('box-shadow');
        chatButton.style.removeProperty('border-radius');
        chatButton.style.removeProperty('display'); // ✅ reset inline-block fix
      }

      chatButton.addEventListener('focus', onFocus);
      chatButton.addEventListener('blur', onBlur);

      cleanupRef.current = () => {
        // Move launcher back to original position
        if (originalNext && originalParent.contains(originalNext)) {
          originalParent.insertBefore(launcher, originalNext);
        } else if (originalParent) {
          originalParent.appendChild(launcher);
        }
        chatButton.removeEventListener('focus', onFocus);
        chatButton.removeEventListener('blur', onBlur);
        onBlur();
      };
    }

    const chatButton = document.querySelector(chatSelector);
    if (chatButton) {
      setup(chatButton);
      return () => cleanupRef.current?.();
    }

    // Chat widget not yet in DOM — watch for it
    const observer = new MutationObserver((_mutations, obs) => {
      const btn = document.querySelector(chatSelector);
      if (btn) {
        obs.disconnect();
        setup(btn);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      cleanupRef.current?.();
    };
  }, [chatSelector, launcherId, faqId, footerId]);
}
